import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spApi } from '@/lib/api-client';

export type RecurringPaymentFrequency =
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'quarterly'
  | 'half_yearly'
  | 'yearly'
  | 'once';

export const RECURRING_PAYMENT_FREQUENCIES: RecurringPaymentFrequency[] = [
  'daily',
  'weekly',
  'bi_weekly',
  'monthly',
  'quarterly',
  'half_yearly',
  'yearly',
];

export const RECURRING_PAYMENT_FREQUENCY_LABELS: Record<RecurringPaymentFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  once: 'One-time',
};

export type RecurringPaymentStatus = 'active' | 'paused' | 'cancelled' | 'completed';

export interface RecurringPayment {
  id: string;
  tenantId: string;
  locationId: string | null;
  merchantId: string | null;
  payeeId: string;
  fundingAccountId: string;
  amount: string;
  currency: string;
  memo: string | null;
  statementDescriptor: string | null;
  paymentMethod: string;
  frequency: RecurringPaymentFrequency;
  startAt: string;
  endAt: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  status: RecurringPaymentStatus;
  createdAt: string;
  updatedAt: string;
}

interface ListParams {
  status?: RecurringPaymentStatus;
  payeeId?: string;
  locationId?: string;
  limit?: number;
  offset?: number;
}

// `enabled: false` skips the request for a viewer without Recurring & Scheduled.
export function useRecurringPayments(params?: ListParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['sp', 'recurring-payments', params],
    queryFn: () =>
      spApi.get<{ data: RecurringPayment[]; total: number; limit: number; offset: number }>(
        '/recurring-payments',
        params as Record<string, string | number | undefined>,
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateRecurringPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => spApi.post<{ data: RecurringPayment }>('/recurring-payments', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'recurring-payments'] });
    },
  });
}

export function useUpdateRecurringPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      spApi.put<{ data: RecurringPayment }>(`/recurring-payments/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'recurring-payments'] });
    },
  });
}

export function usePauseRecurringPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.post<{ data: RecurringPayment }>(`/recurring-payments/${id}/pause`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'recurring-payments'] }),
  });
}

export function useResumeRecurringPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.post<{ data: RecurringPayment }>(`/recurring-payments/${id}/resume`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'recurring-payments'] }),
  });
}

export function useCancelRecurringPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.delete<{ data: RecurringPayment }>(`/recurring-payments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'recurring-payments'] }),
  });
}
